<script setup lang="ts">
import type { ToastHtmlElement } from "@/types"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, ref, useTemplateRef } from "vue"
import { z } from "zod"
import { useGenerateQRCode } from "@/api"
import {
  Alert,
  Button,
  ButtonLink,
  Checkbox,
  ColourPicker,
  Select,
  Toast,
} from "@/components/ui"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/custom-qr", value => Number(value))

const { t } = useTranslation()
const codeUrl = ref<string>("")

const successToast = useTemplateRef<ToastHtmlElement | null>("successToast")
const errorToast = useTemplateRef<ToastHtmlElement | null>("errorToast")

const validationSchema = toTypedSchema(z.object({
  background: z.string().min(1, t("custom_qr_page.form.background_required")),
  foreground: z.string().min(1, t("custom_qr_page.form.foreground_required")),
  format: z.enum(["png", "jpg", "svg"], { required_error: t("custom_qr_page.form.format_required") }),
  transparent: z.boolean().optional(),
}))

const { values, handleSubmit } = useForm({
  validationSchema,
  initialValues: {
    background: "#FFF",
    foreground: "#000",
    format: "png",
    transparent: false,
  },
})

const formatItems = computed(() => [
  { label: "PNG", value: "png" },
  { label: "JPEG", value: "jpg" },
  { label: "SVG", value: "svg" },
])

const { mutate, isPending } = useGenerateQRCode()

const assetUrl = computed<string>(() => {
  return codeUrl.value
})

function generateSnowflake() {
  const timestamp = Date.now()
  const randomNum = Math.floor(Math.random() * 1e9)
  return `&flake=${timestamp}-${randomNum}`
}

const onSubmit = handleSubmit((v) => {
  codeUrl.value = ""

  if (!eventVaultId.value) {
    errorToast.value?.showToast()
    return
  }

  const fields = {
    format: v.format,
    useTransparentBackground: v.transparent ?? false,
    foregroundColor: v.foreground,
    backgroundColor: v.background,
  }

  mutate({
    eventVaultId: eventVaultId.value,
    fields,
  }, {
    onSuccess(data) {
      if (data) {
        codeUrl.value = data + generateSnowflake()
      }

      successToast.value?.showToast({
        title: t("custom_qr_page.success_title"),
        content: t("custom_qr_page.success_content"),
        icon: "CircleCheck",
      })
    },
    onError() {
      errorToast.value?.showToast()
    },
  })
})
</script>

<template>
  <SidebarHostEventLayout>
    <h1 class="font-comfortaa font-bold text-xl mb-8">
      {{ t('custom_qr_page.title') }}
    </h1>
    <Alert :closable="false" class="mb-4">
      <p class="mb-3">
        {{ t('custom_qr_page.description.p1') }}
      </p>
      <p>{{ t('custom_qr_page.description.p2') }}</p>
    </Alert>
    <div v-show="isPending" class="w-full flex flex-col gap-8">
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-36 h-12" />
    </div>
    <form v-show="!isPending" @submit.prevent="onSubmit">
      <div class="w-full flex flex-col gap-8">
        <ColourPicker name="background" :label="t('custom_qr_page.form.background_label')" />
        <ColourPicker name="foreground" :label="t('custom_qr_page.form.foreground_label')" />
        <Select name="format" :label="t('custom_qr_page.form.format_label')" :items="formatItems" />
        <Checkbox
          v-if="values.format === 'png'" name="transparent"
          :label="t('custom_qr_page.form.transparent_label')"
        />
        <div>
          <Button>{{ t('custom_qr_page.form.button_label') }}</Button>
        </div>
      </div>
    </form>
    <Transition name="gpx-fade" mode="out-in">
      <div v-if="codeUrl">
        <hr class="my-8">
        <div class="flex flex-col items-start gap-5 mt-4">
          <img :src="assetUrl" alt="Custom QR code" class="w-full max-w-96 aspect-square">
          <ButtonLink :href="assetUrl" download prefix-icon="Download">
            {{ t('download') }}
          </ButtonLink>
        </div>
      </div>
    </Transition>

    <Toast ref="successToast" variant="success" />
    <Toast ref="errorToast" variant="danger" />
  </SidebarHostEventLayout>
</template>
